import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMutationAppointmentCreateAsMember,
  useMutationAppointmentUpdateAsMember,
  useQueryAppointableAsPublic,
  useQueryAppointmentIdAsMember,
  useQueryMemberGetCurrentMembershipSubscriptionAsMember,
} from "@gymflow/api";
import { cn, formatCurrency } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature } from "apps/portal/src/routes";
import { DateTime, Duration, Interval } from "luxon";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import useGymflowModels from "../../../store";
import {
  Button,
  CalendarIcon,
  ClockIcon,
  CreditsIcon,
  Spinner,
} from "../../atoms";
import { FacilityAvatar } from "../../molecules/FacilityAvatar";
import { StaffAvatar } from "../../molecules/StaffAvatar";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import HostedPagesOverviewCard from "../components/HostedPagesOverviewCard";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";

export type HostedPagesAppointmentReviewProps = { mode: "edit" | "create" };

export const HostedPagesAppointmentReview: React.FC<
  HostedPagesAppointmentReviewProps
> = ({ mode }) => {
  const { appointableId, staffOrFacilityId, timestamp, appointmentId } =
    useParams<{
      appointableId?: string;
      appointmentId?: string;
      staffOrFacilityId: string;
      timestamp: string;
    }>();
  const {
    club: { defaultCurrency, timezone },
  } = useClubSettings();
  const history = useHistory();
  const startTimestamp = DateTime.fromISO(timestamp).setZone(timezone);
  const { createSiteOrEmbedLink, isEmbed } = usePortalRoutes();
  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(RouteFeature.Appointables),
  );
  const { t } = useTranslation();
  const { api } = useGymflowModels();

  const { data: dataAppointment, isLoading: isLoadingAppointment } =
    useQueryAppointmentIdAsMember({
      api,
      id: Number(appointmentId),
      enabled: mode === "edit",
    });

  const { data: appointable, isLoading: isLoadingAppointable } =
    useQueryAppointableAsPublic({
      api,
      id:
        mode === "create" && appointableId
          ? parseInt(appointableId)
          : dataAppointment?.appointable.id,
    });

  const isLoading = isLoadingAppointment || isLoadingAppointable;

  const createAppointmentMutation = useMutationAppointmentCreateAsMember({
    api,
  });
  const updateAppointmentMutation = useMutationAppointmentUpdateAsMember({
    api,
  });
  const { data: activeSubscription } =
    useQueryMemberGetCurrentMembershipSubscriptionAsMember(
      {
        api,
      },
      { refetchOnWindowFocus: true },
    );
  const { toast } = useContext(ToastContext);
  const [parent] = useAutoAnimate();

  const defaultPrice =
    appointable?.price && formatCurrency(appointable.price, defaultCurrency);

  const membersPrice =
    appointable?.membersOnlyPrice &&
    formatCurrency(appointable.membersOnlyPrice, defaultCurrency);

  return (
    <div
      className={cn("track-height flex w-full flex-col overflow-y-auto", {
        "h-fit": isEmbed,
        "h-full": !isEmbed,
      })}
    >
      <HostedPagesBackHeader
        label={t("pages.hostedPagesAppointableOverView.header")}
      />
      <div
        ref={parent}
        className={cn(
          "flex flex-col items-center bg-gray-50 dark:bg-gray-800",
          {
            "h-fit": isEmbed,
            "h-full": !isEmbed,
          },
        )}
      >
        {isLoading && <Spinner />}
        {!isLoading && appointable && (
          <HostedPagesOverviewCard.Container>
            <div className="flex w-full flex-col items-center gap-6">
              <HostedPagesOverviewCard.Header>
                <HostedPagesOverviewCard.HeaderTitle>
                  <div className="dark:text-gray-0 flex w-full text-xl font-bold text-gray-950">
                    {appointable.name}
                  </div>
                  <div className="flex flex-row items-center gap-1">
                    <ClockIcon
                      pathClassName="stroke-gray-500 stroke-1"
                      className="size-5"
                    />
                    <div className="dark:text-gray-0 text-base font-semibold text-gray-950">
                      {Duration.fromObject({
                        minutes: appointable.duration,
                      })
                        .rescale()
                        .toHuman({
                          unitDisplay: "narrow",
                          listStyle: "short",
                        })}
                    </div>
                  </div>
                </HostedPagesOverviewCard.HeaderTitle>
                {appointable.description && (
                  <HostedPagesOverviewCard.HeaderDescription>
                    {appointable.description}
                  </HostedPagesOverviewCard.HeaderDescription>
                )}
              </HostedPagesOverviewCard.Header>
              <div className="flex w-full flex-col gap-1">
                {["STAFF", "STAFF_AND_FACILITY", "FACILITY_AND_STAFF"].includes(
                  appointable.availabilityType,
                ) && (
                  <StaffAvatar
                    userStaff={
                      appointable.appointableHosts.find(
                        (e) => e.id === staffOrFacilityId,
                      ) ?? {
                        firstName: "No",
                        lastName: "Preference",
                      }
                    }
                  />
                )}
                {[
                  "FACILITY",
                  "STAFF_AND_FACILITY",
                  "FACILITY_AND_STAFF",
                ].includes(appointable.availabilityType) && (
                  <FacilityAvatar
                    facility={
                      appointable.appointableFacilities.find(
                        (e) => e.id === Number(staffOrFacilityId),
                      ) ?? {
                        name: "No Preference",
                      }
                    }
                  />
                )}
                <div className="flex flex-row items-center gap-2">
                  <CalendarIcon
                    pathClassName="stroke-gray-500"
                    className="size-5"
                  />
                  <div className="dark:text-gray-0 line-clamp-1 text-sm font-semibold text-gray-950">
                    {startTimestamp.toLocaleString({
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <ClockIcon
                    pathClassName="stroke-gray-500"
                    className="size-5"
                  />
                  <div className="dark:text-gray-0 line-clamp-1 text-sm font-semibold text-gray-950">
                    {Interval.fromDateTimes(
                      startTimestamp,
                      startTimestamp.plus({ minutes: appointable.duration }),
                    ).toLocaleString(DateTime.TIME_SIMPLE)}
                  </div>
                </div>
              </div>
              <div
                className="flex w-full border border-gray-300 dark:border-gray-700 "
                style={{
                  borderImage:
                    "repeating-linear-gradient(to right, rgb(203, 213, 225) 0px, rgb(203, 213, 225) 10px, transparent 5px, transparent 15px) 1",
                }}
              />
              <div className="flex w-full flex-row justify-between">
                <div className="dark:text-gray-0 text-xl font-bold text-gray-950">
                  {t("page.hostedPagesAppointableReview.amountDue")}
                </div>
                <div className="flex flex-row items-center justify-start gap-2">
                  {appointable.price !== undefined && (
                    <div className="dark:text-gray-0 text-lg font-bold text-gray-950">
                      {!!activeSubscription && membersPrice !== undefined
                        ? membersPrice
                        : defaultPrice}
                    </div>
                  )}
                  {appointable.price !== undefined &&
                    appointable.sessionCost !== undefined && (
                      <div className="text-gray-500">
                        {t("pages.hostedPagesAppointables.or")}
                      </div>
                    )}
                  {appointable.sessionCost !== undefined && (
                    <div className="dark:text-gray-0 flex flex-row items-center gap-1 text-lg font-bold text-gray-950">
                      {appointable.sessionCost}
                      <CreditsIcon pathClassName="stroke-secondary-500" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {(mode === "edit" ||
              appointable.pricingModel === "FREE" ||
              appointable.price === 0 ||
              !appointable.paymentUpfront) && (
              <Button
                className="w-full"
                onClick={async () => {
                  if (mode === "create") {
                    await createAppointmentMutation.mutateAsync({
                      appointableId: appointable.id,
                      startDate:
                        startTimestamp
                          .toUTC()
                          .toISO({ suppressMilliseconds: true }) ?? "",
                      status: "BOOKED",
                      ...(staffOrFacilityId === "no-preference"
                        ? {}
                        : ["STAFF", "STAFF_AND_FACILITY"].includes(
                              appointable.availabilityType,
                            )
                          ? {
                              appointmentHostId: staffOrFacilityId,
                            }
                          : {
                              appointmentFacilityId: Number(staffOrFacilityId),
                            }),
                    });
                    toast({
                      message: t("pages.hostedPagesAppointableReview.booked"),
                      intent: "success",
                    });
                    history.push(
                      createSiteOrEmbedLink(
                        RouteFeature.SiteProfileBookingAppointments,
                      ),
                    );
                  } else {
                    const isNoPreference =
                      staffOrFacilityId === "no-preference";

                    await updateAppointmentMutation.mutateAsync({
                      appointmentId: Number(appointmentId),
                      appointment: {
                        startDate:
                          startTimestamp
                            .toUTC()
                            .toISO({ suppressMilliseconds: true }) ?? "",
                        ...(["STAFF", "STAFF_AND_FACILITY"].includes(
                          appointable.availabilityType,
                        )
                          ? {
                              appointmentHostId: isNoPreference
                                ? undefined
                                : staffOrFacilityId,
                              appointmentHostIdUnset: isNoPreference,
                              appointmentFacilityIdUnset:
                                appointable.availabilityType === "STAFF"
                                  ? undefined
                                  : true,
                            }
                          : {
                              appointmentFacilityId: isNoPreference
                                ? undefined
                                : Number(staffOrFacilityId),
                              appointmentFacilityIdUnset: isNoPreference,
                              appointmentHostIdUnset:
                                appointable.availabilityType === "FACILITY"
                                  ? undefined
                                  : true,
                            }),
                      },
                    });
                    toast({
                      message: t(
                        "pages.hostedPagesAppointableReview.rescheduled",
                      ),
                      intent: "success",
                    });
                    history.push(
                      createSiteOrEmbedLink(
                        RouteFeature.SiteProfileBookingAppointmentId,
                      ).replace(":appointmentId", String(appointmentId)),
                    );
                  }
                }}
                intent="secondary"
              >
                {mode === "edit"
                  ? t("portal.common.reschedule")
                  : appointable.pricingModel === "FREE"
                    ? t("portal.common.getForFree")
                    : t("page.hostedPagesAppointableReview.confirmBooking")}
              </Button>
            )}
            {!(
              mode === "edit" ||
              appointable.pricingModel === "FREE" ||
              appointable.price === 0 ||
              !appointable.paymentUpfront
            ) && (
              <Button
                className="w-full"
                link={createSiteOrEmbedLink(
                  RouteFeature.AppointableIdCheckout.replace(
                    ":appointableId",
                    String(appointableId),
                  )
                    .replace(":staffOrFacilityId", staffOrFacilityId)
                    .replace(":timestamp", timestamp ?? ""),
                )}
                intent="secondary"
              >
                {t("page.hostedPagesAppointableReview.goToCheckout")}
              </Button>
            )}
          </HostedPagesOverviewCard.Container>
        )}
      </div>
    </div>
  );
};
